import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import Button from '@mui/material/Button';
import TranslateIcon from '@mui/icons-material/Translate';
import { LanguageContext } from '../contexts/LanguageContext';
import { color, down, fontSize, hoverScale } from '../styles/tokens';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { t } from '../lib/localize';
import { FB } from '../content/fallbacks';
import { A11Y } from '../content/a11y';

const StyledFooter = styled.footer`
  ${({ $overlay }) => $overlay && css`
    position: fixed;
    bottom: 20px;
    left: 0;
    width: 100%;
    pointer-events: none;
    z-index: 10000;

    ${down('smPlus')} { display: none; }
  `}

  background-color: transparent;
  color: ${color.textSecondary};
  padding: 20px;
  text-align: center;
  user-select: none;

  .footer-content {
    max-width: 1200px;
    margin: 0 auto ${({ $overlay }) => ($overlay ? '0' : '20px')};
    display: flex;
    justify-content: space-between;
    align-items: center;
    pointer-events: none;
  }

  ${down('wide')} {
    .footer-content { margin-right: 50px; }
  }
  ${down('smPlus')} {
    .footer-content { flex-direction: column; margin-right: 0; }
  }
`;

/* Atom brand mark — reuses the same U+269B ⚛ glyph + U+FE0E text-presentation
   selector pair as the hero ("Results Delivered ⚛"). Sits between the legal
   entity name and the rights clause as a quiet brand signature. Inherits
   footer text color via currentColor so it reads as punctuation rather than
   decoration. */
const AtomMark = styled.span.attrs({
  'aria-hidden': true,
  children: '\u269B\uFE0E',
})`
  display: inline-block;
  font-size: 1.2em;
  color: currentColor;
  margin: 0 0.45em;
  vertical-align: -0.02em;
`;

const StyledButton = styled(Button)`
  && {
    background-color: ${color.surfaceOverlay};
    color: ${({ $overlay }) => ($overlay ? color.white : color.textSecondary)};
    border: 1px solid ${color.borderFaint};
    padding: 6px 25px;
    border-radius: 30px;
    text-transform: none;
    font-size: ${fontSize.base};
    transition: transform .5s;
    pointer-events: auto;
    position: relative;
    z-index: 10001;
    margin-top: 20px;

    &:hover {
      background-color: ${color.surfaceOverlay};
      transform: scale(${hoverScale.md});
    }
  }
`;

export default function Footer({ overlay = false }) {
  const { language, setLanguage } = useContext(LanguageContext);
  const settings = useSiteSettings();

  const toggleLanguage = () => {
    setLanguage((lang) => (lang === 'EN' ? 'FR' : 'EN'));
  };

  /* The year is derived, not authored — an editor should never be able
     to make the copyright line say last year. */
  const year = new Date().getFullYear();
  const legalName = t(settings?.legalName, language, FB.site.legalName);
  const rights = t(settings?.rightsReserved, language, FB.site.rightsReserved);

  return (
    <StyledFooter $overlay={overlay}>
      <div className="footer-content">
        {!overlay ? (
          <p>
            &copy;{year} Soquelec {legalName}
            <AtomMark />
            {rights}
          </p>
        ) : <p aria-hidden="true" />}

        {/* Spacer for flex layout */}
        <div />

        {/* The button label is the language you switch TO, so it is not
            a translated string — it is always the other language's own
            endonym. The aria-label is translated, from the a11y map. */}
        <StyledButton
          $overlay={overlay}
          disableElevation
          onClick={toggleLanguage}
          aria-label={t(A11Y.changeLanguage, language)}
          startIcon={<TranslateIcon aria-hidden="true" focusable="false" />}
        >
          {language === 'EN' ? 'Français' : 'English'}
        </StyledButton>
      </div>
    </StyledFooter>
  );
}